import Axios from "axios";
import store from "../store";
import router from "../router";

let isRefreshing = false;
let refreshSubscribers = [];
let exactRefreshTimeout = null;
let lastScheduledRefreshTime = 0;
let safetyCheckInterval = null;
let isStoreWatchRegistered = false;

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token) {
  refreshSubscribers.forEach((cb) => cb(null, token));
  refreshSubscribers = [];
}

function onRefreshFailed(err) {
  refreshSubscribers.forEach((cb) => cb(err, null));
  refreshSubscribers = [];
}

/**
 * Decode JWT token payload safely
 */
export function parseJwt(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

/**
 * Safely parse and retrieve the refreshExpiredDate timestamp (in ms) from user or login response.
 * Prioritizes refreshExpiredDate, with fallback to expiredDate.
 */
export function getRefreshExpiredDate(user) {
  if (!user) return null;
  const target = user.model || user;

  // 1. Primary: refreshExpiredDate
  const refreshVal =
    target.refreshExpiredDate ||
    target.refreshexpireddate ||
    target.refresh_expired_date ||
    target.refreshExpireDate ||
    user.refreshExpiredDate ||
    user.refreshexpireddate ||
    user.refresh_expired_date ||
    user.refreshExpireDate;

  // 2. Fallback: expiredDate
  const val =
    refreshVal ||
    target.expiredDate ||
    target.expireddate ||
    target.expired_date ||
    target.expireDate ||
    user.expiredDate ||
    user.expireddate ||
    user.expired_date ||
    user.expireDate;

  if (val) {
    if (typeof val === "number") {
      // If unix timestamp in seconds (less than 1e11), convert to milliseconds
      return val < 1e11 ? val * 1000 : val;
    }
    if (typeof val === "string") {
      const trimmed = val.trim();
      if (/^\d+$/.test(trimmed)) {
        const num = Number(trimmed);
        return num < 1e11 ? num * 1000 : num;
      }
      const time = new Date(trimmed).getTime();
      if (!isNaN(time) && time > 0) {
        return time;
      }
    }
    if (val instanceof Date) {
      const time = val.getTime();
      if (!isNaN(time) && time > 0) return time;
    }
  }

  // Fallback: decode JWT access token if available
  const token = user.access_token || user.accessToken || user.token;
  if (token && typeof token === "string" && token.includes(".")) {
    const jwt = parseJwt(token);
    if (jwt && jwt.exp) {
      return jwt.exp * 1000;
    }
  }

  return null;
}

export const getExpiredDate = getRefreshExpiredDate;

/**
 * Schedule token refresh to POST to /api/Users/RefreshToken 3 minutes before refreshExpiredDate
 */
export function scheduleTokenRefresh(user = null) {
  if (exactRefreshTimeout) {
    clearTimeout(exactRefreshTimeout);
    exactRefreshTimeout = null;
  }

  const currentUser = user || store.get("user");
  if (!currentUser) return;

  const refreshTokenValue =
    currentUser.refresh_token ||
    currentUser.refreshToken ||
    currentUser.token ||
    currentUser.access_token ||
    currentUser.accessToken;

  if (!refreshTokenValue) return;

  const refreshExpiredTime = getRefreshExpiredDate(currentUser);
  if (!refreshExpiredTime) {
    console.warn(
      "[Auth] No refreshExpiredDate found in user session for scheduling refresh",
    );
    return;
  }

  const now = Date.now();
  const THREE_MINUTES_MS = 3 * 60 * 1000; // 3 minutes
  const targetTime = refreshExpiredTime - THREE_MINUTES_MS;
  const delay = targetTime - now;

  if (delay <= 0) {
    // Within 3 minutes of refreshExpiredDate (or past targetTime)
    // Throttle immediate execution to avoid tight loops if failure occurs
    if (now - lastScheduledRefreshTime > 10 * 1000) {
      lastScheduledRefreshTime = now;
      refreshToken().catch((err) =>
        console.warn("[Auth] Immediate token refresh failed:", err),
      );
    }
  } else if (delay < 2147483647) {
    // Standard setTimeout (max 32-bit int = ~24.8 days)
    exactRefreshTimeout = setTimeout(async () => {
      lastScheduledRefreshTime = Date.now();
      try {
        await refreshToken();
      } catch (err) {
        console.warn("[Auth] Scheduled token refresh failed:", err);
      }
    }, delay);
  }
}

/**
 * Save user session to Vuex store and native Flutter storage
 */
export async function saveUserSession(updatedUser) {
  if (!updatedUser) return;
  store.set("user", updatedUser);
  if (updatedUser.salehistory_url) {
    store.set("salehistory_url", updatedUser.salehistory_url);
  } else if (updatedUser.salehistoryUrl) {
    store.set("salehistory_url", updatedUser.salehistoryUrl);
  }

  try {
    let fl = window["flutter_inappwebview"];
    if (!fl) {
      for (let i = 0; i < 10; i++) {
        await new Promise((r) => setTimeout(r, 50));
        if (window["flutter_inappwebview"]) {
          fl = window["flutter_inappwebview"];
          break;
        }
      }
    }
    if (fl) {
      await fl.callHandler("setdata", "loginResponse", updatedUser);
      const uname = updatedUser.username || updatedUser.userName;
      if (uname) {
        await fl.callHandler("setdata", "username", `${uname}`);
      }
      try {
        const isBranch =
          updatedUser.userCategoryId == 2 || updatedUser.usercategoryid == 2;
        const key = isBranch ? "branchProfile" : "customerProfile";
        await fl.callHandler("writesecure", key, JSON.stringify([updatedUser]));
      } catch (err) {
        console.error("errorwritesecureprofile", JSON.stringify(err));
      }
    }
  } catch (e) {
    console.error("errorsaveusersession", JSON.stringify(e));
  }

  // Schedule timer for the updated expiration date
  scheduleTokenRefresh(updatedUser);
}

/**
 * Clear user session from Vuex store and native Flutter storage
 */
export async function clearUserSession(redirectTo = null) {
  if (exactRefreshTimeout) {
    clearTimeout(exactRefreshTimeout);
    exactRefreshTimeout = null;
  }

  try {
    let fl = window["flutter_inappwebview"];
    if (!fl) {
      for (let i = 0; i < 10; i++) {
        await new Promise((r) => setTimeout(r, 50));
        if (window["flutter_inappwebview"]) {
          fl = window["flutter_inappwebview"];
          break;
        }
      }
    }
    if (fl) {
      await fl.callHandler("setdata", "loginResponse", null);
      await fl.callHandler("setdata", "username", null);
      await fl.callHandler("setdata", "machineid", null);
      await fl.callHandler("setdata", "salehistory_url", null);
    }
  } catch (e) {
    console.error("errorclearusersession", JSON.stringify(e));
  }

  store.set("salehistory_url", null);
  store.set("machineid", null);
  store.set("user", null);

  if (redirectTo) {
    if (router.currentRoute.path !== redirectTo) {
      router.replace({ path: redirectTo });
    }
  } else if (
    router.currentRoute.path !== "/" &&
    router.currentRoute.path !== "/login"
  ) {
    router.replace({ path: "/" });
  }
}

/**
 * Refresh access token by posting to /api/Users/RefreshToken
 * Payload format:
 * {
 *   "accessType": 2,
 *   "token": ""
 * }
 */
export async function refreshToken(customToken = null) {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh((err, newToken) => {
        if (err) {
          return reject(err);
        }
        resolve(newToken);
      });
    });
  }

  isRefreshing = true;

  try {
    let user = store.get("user");
    if (
      !user &&
      typeof window !== "undefined" &&
      window["flutter_inappwebview"]
    ) {
      try {
        const cached = await window["flutter_inappwebview"].callHandler(
          "getdata",
          "loginResponse",
        );
        if (cached) {
          store.set("user", cached);
          user = cached;
        }
      } catch (e) {
        console.warn("Error getting user from flutter in refreshToken:", e);
      }
    }

    const tokenToUse =
      customToken ||
      user?.refresh_token ||
      user?.refreshToken ||
      user?.token ||
      user?.access_token ||
      user?.accessToken;

    if (!tokenToUse) {
      throw new Error("No refresh token available");
    }

    const payload = {
      accessType: 2,
      token: tokenToUse,
    };

    const headers = {
      "Content-Type": "application/json",
    };

    const res = await Axios.post("/api/Users/RefreshToken", payload, {
      headers,
      _skipAuthRefresh: true,
      timeout: 15000,
    });

    const data = res?.data || res;
    if (data?.didError) {
      throw new Error(data?.errorMessage || "RefreshToken failed");
    }

    const responseData = data?.model !== undefined ? data.model : data;
    let newAccessToken = null;
    let newRefreshToken = null;

    if (typeof responseData === "string") {
      newAccessToken = responseData;
      newRefreshToken = tokenToUse;
    } else if (responseData && typeof responseData === "object") {
      newAccessToken =
        responseData.access_token ||
        responseData.accessToken ||
        responseData.token;
      newRefreshToken =
        responseData.refresh_token || responseData.refreshToken || tokenToUse;
    }

    if (!newAccessToken) {
      throw new Error("Invalid response from RefreshToken endpoint");
    }

    const newRefreshExpiredDate =
      (responseData && typeof responseData === "object"
        ? responseData.refreshExpiredDate ||
          responseData.refreshexpireddate ||
          responseData.refresh_expired_date ||
          responseData.refreshExpireDate
        : null) ||
      data?.refreshExpiredDate ||
      data?.refreshexpireddate ||
      data?.refresh_expired_date;

    const finalRefreshExpiredDate =
      newRefreshExpiredDate ||
      user?.refreshExpiredDate ||
      user?.refreshexpireddate ||
      user?.refresh_expired_date ||
      user?.refreshExpireDate;

    const newExpiredDate =
      (responseData && typeof responseData === "object"
        ? responseData.expiredDate ||
          responseData.expireddate ||
          responseData.expired_date ||
          responseData.expireDate
        : null) ||
      data?.expiredDate ||
      data?.expireddate ||
      data?.expired_date;

    let finalExpiredDate = newExpiredDate;
    if (
      !finalExpiredDate &&
      newAccessToken &&
      typeof newAccessToken === "string" &&
      newAccessToken.includes(".")
    ) {
      const jwt = parseJwt(newAccessToken);
      if (jwt && jwt.exp) {
        finalExpiredDate = new Date(jwt.exp * 1000).toISOString();
      }
    }
    if (!finalExpiredDate) {
      finalExpiredDate =
        user?.expiredDate || user?.expireddate || user?.expired_date;
    }

    const updatedUser = {
      ...(user || {}),
      ...(responseData && typeof responseData === "object" ? responseData : {}),
      access_token: newAccessToken,
      accessToken: newAccessToken,
      refresh_token: newRefreshToken,
      refreshToken: newRefreshToken,
      ...(finalRefreshExpiredDate
        ? {
            refreshExpiredDate: finalRefreshExpiredDate,
            refreshexpireddate: finalRefreshExpiredDate,
            refresh_expired_date: finalRefreshExpiredDate,
          }
        : {}),
      ...(finalExpiredDate
        ? {
            expiredDate: finalExpiredDate,
            expireddate: finalExpiredDate,
            expired_date: finalExpiredDate,
          }
        : {}),
    };

    await saveUserSession(updatedUser);
    isRefreshing = false;
    onRefreshed(newAccessToken);
    return newAccessToken;
  } catch (error) {
    isRefreshing = false;
    onRefreshFailed(error);
    throw error;
  }
}

/**
 * Setup lifecycle listeners (visibility, focus, periodic safety check, store watcher)
 */
export function setupAutoRefreshToken() {
  if (typeof window !== "undefined") {
    // Re-evaluate timer when user returns to the app / tab
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        const user = store.get("user");
        if (user) {
          scheduleTokenRefresh(user);
        }
      }
    });

    window.addEventListener("focus", () => {
      const user = store.get("user");
      if (user) {
        scheduleTokenRefresh(user);
      }
    });

    // Watch store user mutations to auto-schedule on login/refresh and clear on logout
    if (store && store.watch && !isStoreWatchRegistered) {
      isStoreWatchRegistered = true;
      store.watch(
        (state) => state.user,
        (newUser) => {
          if (newUser) {
            scheduleTokenRefresh(newUser);
          } else if (exactRefreshTimeout) {
            clearTimeout(exactRefreshTimeout);
            exactRefreshTimeout = null;
          }
        },
      );
    }

    // Periodic safety check every 30 seconds (acts as backup for mobile sleep/throttling)
    if (safetyCheckInterval) {
      clearInterval(safetyCheckInterval);
    }
    safetyCheckInterval = setInterval(() => {
      const user = store.get("user");
      if (user) {
        const refreshExpiredTime = getRefreshExpiredDate(user);
        if (refreshExpiredTime) {
          const now = Date.now();
          const targetTime = refreshExpiredTime - 3 * 60 * 1000;
          if (now >= targetTime && now < refreshExpiredTime + 60 * 60 * 1000) {
            if (now - lastScheduledRefreshTime > 30 * 1000) {
              lastScheduledRefreshTime = now;
              refreshToken().catch((e) =>
                console.warn("[Auth] Safety check refresh failed:", e),
              );
            }
          }
        }
      }
    }, 30 * 1000);

    // Initial check on app startup
    setTimeout(() => {
      const user = store.get("user");
      if (user) {
        scheduleTokenRefresh(user);
      }
    }, 1500);
  }
}
